/**
 * Canvas blur effect bakers for Editing Tools → Blur.
 * Distinct paths for gaussian-family, motion, hex pixelate, and square pixel.
 */

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create canvas");
  return { canvas, ctx };
}

/**
 * Map slider 0–100 → pixel radius relative to image size.
 */
function resolveBakeRadiusPx(amount, iw, ih) {
  const a = Math.max(0, Math.min(100, Math.round(Number(amount) || 0)));
  if (a <= 0) return 0;
  const shortSide = Math.min(iw, ih);
  return Math.max(1, (a / 100) * shortSide * 0.06);
}

/** Soft CSS-style blur. Avoids scale-up "grow" (that creates ghost silhouettes). */
function applyFilterBlur(sourceCanvas, radius, extraFilter = "") {
  const r = Math.max(0, Number(radius) || 0);
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const { canvas, ctx } = createCanvas(iw, ih);
  if (r <= 0 && !extraFilter) {
    ctx.drawImage(sourceCanvas, 0, 0);
    return canvas;
  }

  const workScale = r > 16 ? Math.min(1, 16 / r) : 1;
  const ww = Math.max(1, Math.round(iw * workScale));
  const wh = Math.max(1, Math.round(ih * workScale));
  const workRadius = r * workScale;

  const { canvas: work, ctx: wctx } = createCanvas(ww, wh);
  wctx.imageSmoothingEnabled = true;
  wctx.drawImage(sourceCanvas, 0, 0, ww, wh);

  const pad = Math.ceil(Math.max(workRadius, 1) * 2);
  const { canvas: padded, ctx: pctx } = createCanvas(ww + pad * 2, wh + pad * 2);
  // Edge-extend by stretching border pixels (no content scale-up).
  pctx.imageSmoothingEnabled = false;
  pctx.drawImage(work, 0, 0, ww, 1, pad, 0, ww, pad); // top
  pctx.drawImage(work, 0, wh - 1, ww, 1, pad, pad + wh, ww, pad); // bottom
  pctx.drawImage(work, 0, 0, 1, wh, 0, pad, pad, wh); // left
  pctx.drawImage(work, ww - 1, 0, 1, wh, pad + ww, pad, pad, wh); // right
  pctx.drawImage(work, 0, 0, 1, 1, 0, 0, pad, pad); // corners
  pctx.drawImage(work, ww - 1, 0, 1, 1, pad + ww, 0, pad, pad);
  pctx.drawImage(work, 0, wh - 1, 1, 1, 0, pad + wh, pad, pad);
  pctx.drawImage(work, ww - 1, wh - 1, 1, 1, pad + ww, pad + wh, pad, pad);
  pctx.imageSmoothingEnabled = true;
  pctx.drawImage(work, pad, pad);

  const { canvas: blurred, ctx: bctx } = createCanvas(ww + pad * 2, wh + pad * 2);
  const blurPart = workRadius > 0 ? `blur(${workRadius}px)` : "";
  bctx.filter = [blurPart, extraFilter].filter(Boolean).join(" ") || "none";
  bctx.drawImage(padded, 0, 0);
  bctx.filter = "none";

  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(blurred, pad, pad, ww, wh, 0, 0, iw, ih);
  return canvas;
}

/**
 * Directional motion blur (horizontal trail).
 */
function applyMotionBlur(sourceCanvas, amountRadius) {
  const strength = Math.max(0, Math.round(amountRadius));
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const { canvas, ctx } = createCanvas(iw, ih);
  if (strength <= 0) {
    ctx.drawImage(sourceCanvas, 0, 0);
    return canvas;
  }

  const maxWork = 1200;
  const workScale = Math.min(1, maxWork / Math.max(iw, ih));
  const ww = Math.max(1, Math.round(iw * workScale));
  const wh = Math.max(1, Math.round(ih * workScale));
  const workStrength = Math.max(1, strength * workScale);
  const { canvas: work, ctx: wctx } = createCanvas(ww, wh);
  wctx.drawImage(sourceCanvas, 0, 0, ww, wh);

  const { canvas: trail, ctx: tctx } = createCanvas(ww, wh);
  const steps = Math.min(32, Math.max(12, Math.round(workStrength / 2) + 10));
  const half = workStrength * 0.9;
  tctx.clearRect(0, 0, ww, wh);
  tctx.globalAlpha = 1 / steps;
  for (let s = 0; s < steps; s++) {
    const t = steps === 1 ? 0 : s / (steps - 1) - 0.5;
    const ox = half * t * 2;
    tctx.drawImage(work, ox, 0);
  }
  tctx.globalAlpha = 1;

  ctx.imageSmoothingEnabled = true;
  ctx.clearRect(0, 0, iw, ih);
  ctx.drawImage(trail, 0, 0, iw, ih);
  return canvas;
}

/**
 * Average color in a circular patch (subsampled for speed).
 * @returns {[number,number,number,number]|null}
 */
function averagePatch(data, iw, ih, cx, cy, radius) {
  const r = Math.max(1, radius);
  const r2 = r * r;
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(iw - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(ih - 1, Math.ceil(cy + r));
  const step = Math.max(1, Math.floor(r / 3));
  let rs = 0;
  let gs = 0;
  let bs = 0;
  let as = 0;
  let n = 0;
  for (let y = y0; y <= y1; y += step) {
    for (let x = x0; x <= x1; x += step) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > r2) continue;
      const i = (y * iw + x) * 4;
      const a = data[i + 3];
      if (a < 8) continue;
      rs += data[i];
      gs += data[i + 1];
      bs += data[i + 2];
      as += a;
      n += 1;
    }
  }
  if (!n) return null;
  return [
    Math.round(rs / n),
    Math.round(gs / n),
    Math.round(bs / n),
    Math.round(as / n),
  ];
}

function fillPointyHex(ctx, cx, cy, size) {
  const s = size * 1.02;
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + s * Math.cos(angle);
    const y = cy + s * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

function fillCircleCell(ctx, cx, cy, radius) {
  ctx.beginPath();
  ctx.arc(cx, cy, Math.max(0.5, radius), 0, Math.PI * 2);
  ctx.fill();
}

function fillDiamondCell(ctx, cx, cy, half) {
  const h = Math.max(0.5, half);
  ctx.beginPath();
  ctx.moveTo(cx, cy - h);
  ctx.lineTo(cx + h, cy);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx - h, cy);
  ctx.closePath();
  ctx.fill();
}

function mixWithSource(sourceCanvas, effectCanvas, mix01) {
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const m = Math.max(0, Math.min(1, Number(mix01) || 0));
  if (m >= 0.999) return effectCanvas;
  const { canvas, ctx } = createCanvas(iw, ih);
  ctx.drawImage(sourceCanvas, 0, 0);
  if (m <= 0.001) return canvas;
  ctx.globalAlpha = m;
  ctx.drawImage(effectCanvas, 0, 0);
  ctx.globalAlpha = 1;
  return canvas;
}

/**
 * Blend original edges back into the blurred result (detail preserve).
 * @param {number} strength01 0–1
 */
function applyEdgePreserve(sourceCanvas, blurredCanvas, strength01) {
  const s = Math.max(0, Math.min(1, Number(strength01) || 0));
  if (s <= 0.001) return blurredCanvas;

  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const maxWork = 1000;
  const workScale = Math.min(1, maxWork / Math.max(iw, ih));
  const ww = Math.max(1, Math.round(iw * workScale));
  const wh = Math.max(1, Math.round(ih * workScale));

  const { canvas: srcWork, ctx: sctx } = createCanvas(ww, wh);
  const { canvas: blurWork, ctx: bctx } = createCanvas(ww, wh);
  sctx.drawImage(sourceCanvas, 0, 0, ww, wh);
  bctx.drawImage(blurredCanvas, 0, 0, ww, wh);

  const soft = applyFilterBlur(srcWork, Math.max(1.2, Math.min(ww, wh) * 0.004));
  const srcData = sctx.getImageData(0, 0, ww, wh).data;
  const softData = soft.getContext("2d").getImageData(0, 0, ww, wh).data;
  const blurImg = bctx.getImageData(0, 0, ww, wh);
  const out = blurImg.data;

  for (let i = 0; i < out.length; i += 4) {
    const er = Math.abs(srcData[i] - softData[i]);
    const eg = Math.abs(srcData[i + 1] - softData[i + 1]);
    const eb = Math.abs(srcData[i + 2] - softData[i + 2]);
    const edge = Math.min(1, ((er + eg + eb) / 3 / 255) * (1.2 + s * 2.4));
    const t = Math.min(1, edge * s);
    const br = out[i];
    const bg = out[i + 1];
    const bb = out[i + 2];
    out[i] = Math.round(br * (1 - t) + srcData[i] * t);
    out[i + 1] = Math.round(bg * (1 - t) + srcData[i + 1] * t);
    out[i + 2] = Math.round(bb * (1 - t) + srcData[i + 2] * t);
  }
  bctx.putImageData(blurImg, 0, 0);

  if (workScale >= 1) return blurWork;
  const { canvas: full, ctx } = createCanvas(iw, ih);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(blurWork, 0, 0, iw, ih);
  return full;
}

function applyGaussianWithExtras(sourceCanvas, radius, mixPct = 100, edgePreservePct = 0) {
  let blurred = applyFilterBlur(sourceCanvas, radius);
  if (edgePreservePct > 0) {
    blurred = applyEdgePreserve(sourceCanvas, blurred, edgePreservePct / 100);
  }
  return mixWithSource(sourceCanvas, blurred, (mixPct ?? 100) / 100);
}

/**
 * Pixel mosaic with shape / gap / softness / mix.
 * @param {'hex'|'square'|'circle'|'diamond'} shape
 */
function applyPixelMosaic(sourceCanvas, options = {}) {
  const amount = Math.max(0, Math.min(100, Math.round(options.amount || 0)));
  const shape = options.shape || "hex";
  const gap = Math.max(0, Math.min(100, Math.round(options.gap || 0))) / 100;
  const softness = Math.max(0, Math.min(100, Math.round(options.softness || 0)));
  const mix = Math.max(0, Math.min(100, Math.round(options.mix ?? 100))) / 100;

  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  if (amount <= 0) {
    const { canvas, ctx } = createCanvas(iw, ih);
    ctx.drawImage(sourceCanvas, 0, 0);
    return canvas;
  }

  const maxWork = 1400;
  const workScale = Math.min(1, maxWork / Math.max(iw, ih));
  const ww = Math.max(1, Math.round(iw * workScale));
  const wh = Math.max(1, Math.round(ih * workScale));
  const { canvas: work, ctx: wctx } = createCanvas(ww, wh);
  wctx.imageSmoothingEnabled = true;
  wctx.drawImage(sourceCanvas, 0, 0, ww, wh);

  let mosaic;
  if (shape === "square") {
    mosaic = buildSquareMosaic(work, amount, gap);
  } else if (shape === "diamond") {
    mosaic = buildDiamondMosaic(work, amount, gap);
  } else {
    mosaic = buildPackedCellMosaic(work, amount, gap, shape);
  }

  if (softness > 0) {
    const shortSide = Math.min(ww, wh);
    const softPx = Math.max(0.5, (softness / 100) * shortSide * 0.035);
    mosaic = applyFilterBlur(mosaic, softPx);
  }

  if (workScale < 1) {
    const { canvas: full, ctx: fctx } = createCanvas(iw, ih);
    fctx.imageSmoothingEnabled = shape === "square" && gap <= 0.01 ? false : true;
    fctx.drawImage(mosaic, 0, 0, iw, ih);
    mosaic = full;
  }

  return mixWithSource(sourceCanvas, mosaic, mix);
}

function buildSquareMosaic(sourceCanvas, amount, gap01) {
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const shortSide = Math.min(iw, ih);
  const maxBlock = Math.max(16, Math.floor(shortSide / 5));
  const minBlock = 4;
  const block = Math.max(
    minBlock,
    Math.round(minBlock + (amount / 100) * (maxBlock - minBlock))
  );

  const { canvas, ctx } = createCanvas(iw, ih);
  if (gap01 <= 0.01) {
    const sw = Math.max(1, Math.ceil(iw / block));
    const sh = Math.max(1, Math.ceil(ih / block));
    const { canvas: tiny, ctx: tctx } = createCanvas(sw, sh);
    tctx.imageSmoothingEnabled = true;
    tctx.drawImage(sourceCanvas, 0, 0, sw, sh);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, iw, ih);
    ctx.drawImage(tiny, 0, 0, sw, sh, 0, 0, iw, ih);
    ctx.imageSmoothingEnabled = true;
    return canvas;
  }

  // Gapped squares: sample each cell, draw inset rects.
  const src = sourceCanvas.getContext("2d").getImageData(0, 0, iw, ih);
  ctx.clearRect(0, 0, iw, ih);
  const inset = Math.max(1, Math.round(block * gap01 * 0.45));
  const fill = Math.max(1, block - inset * 2);
  for (let y = 0; y < ih; y += block) {
    for (let x = 0; x < iw; x += block) {
      const cx = x + block / 2;
      const cy = y + block / 2;
      const avg = averagePatch(src.data, iw, ih, cx, cy, block * 0.4);
      if (!avg) continue;
      const [r, g, b, a] = avg;
      ctx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
      ctx.fillRect(x + inset, y + inset, fill, fill);
    }
  }
  return canvas;
}

/**
 * Diamond (45° square) mosaic on a staggered (brick) grid.
 * Odd rows shift by half spacing so diamonds interlock — no straight gap alleys.
 * Gap 0 → packed lattice. Gap > 0 shrinks the fill.
 */
function buildDiamondMosaic(sourceCanvas, amount, gap01) {
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const shortSide = Math.min(iw, ih);
  const minHalf = Math.max(3, shortSide / 95);
  const maxHalf = Math.max(10, shortSide / 18);
  const half = minHalf + (amount / 100) * (maxHalf - minHalf);
  // Rotated square of half-diagonal `half` — horizontal pitch is the square side.
  const dx = half * Math.SQRT2 * (gap01 <= 0.01 ? 0.98 : 1);
  // Vertical pitch for interlocking: half of horizontal (brick stagger).
  const dy = dx * 0.5;
  const fillHalf = half * Math.max(0.28, 1 - gap01 * 0.72) * 1.04;

  const src = sourceCanvas.getContext("2d").getImageData(0, 0, iw, ih);
  const { canvas, ctx } = createCanvas(iw, ih);
  ctx.clearRect(0, 0, iw, ih);

  const cols = Math.ceil(iw / dx) + 2;
  const rows = Math.ceil(ih / dy) + 2;
  const sampleR = half * 0.7;

  for (let row = -1; row < rows; row++) {
    const offsetX = row % 2 === 0 ? 0 : dx / 2;
    const cy = row * dy;
    for (let col = -1; col < cols; col++) {
      const cx = col * dx + offsetX;
      const avg = averagePatch(src.data, iw, ih, cx, cy, sampleR);
      if (!avg) continue;
      const [r, g, b, a] = avg;
      ctx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
      fillDiamondCell(ctx, cx, cy, fillHalf);
    }
  }
  return canvas;
}

function buildPackedCellMosaic(sourceCanvas, amount, gap01, cellKind) {
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const shortSide = Math.min(iw, ih);
  const minSize = Math.max(2.5, shortSide / 110);
  const maxSize = Math.max(8, shortSide / 22);
  const size = minSize + (amount / 100) * (maxSize - minSize);
  const dx = size * Math.sqrt(3);
  const dy = size * 1.5;
  // Gap shrinks the drawn cell; spacing stays so gaps read clearly.
  const fillScale = Math.max(0.28, 1 - gap01 * 0.72);

  const src = sourceCanvas.getContext("2d").getImageData(0, 0, iw, ih);
  const { canvas, ctx } = createCanvas(iw, ih);
  ctx.clearRect(0, 0, iw, ih);

  const rows = Math.ceil(ih / dy) + 2;
  const cols = Math.ceil(iw / dx) + 2;
  const sampleR = size * 0.72;

  for (let row = -1; row < rows; row++) {
    const offsetX = row % 2 === 0 ? 0 : dx / 2;
    const cy = row * dy;
    for (let col = -1; col < cols; col++) {
      const cx = col * dx + offsetX;
      const avg = averagePatch(src.data, iw, ih, cx, cy, sampleR);
      if (!avg) continue;
      const [r, g, b, a] = avg;
      ctx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
      if (cellKind === "circle") {
        fillCircleCell(ctx, cx, cy, size * 0.92 * fillScale);
      } else {
        fillPointyHex(ctx, cx, cy, size * fillScale);
      }
    }
  }
  return canvas;
}

/**
 * @param {HTMLImageElement|HTMLCanvasElement} source
 * @param {{ type?: string, amount?: number, pixelShape?: string, pixelGap?: number, pixelSoftness?: number, pixelMix?: number }} options
 * @returns {HTMLCanvasElement}
 */
export function renderBlurEffectCanvas(source, options = {}) {
  const type = options.type || "gaussian";
  const amount = Math.max(0, Math.round(Number(options.amount) || 0));
  const iw = source.naturalWidth || source.width;
  const ih = source.naturalHeight || source.height;
  const { canvas, ctx } = createCanvas(iw, ih);
  ctx.drawImage(source, 0, 0, iw, ih);

  const radius = resolveBakeRadiusPx(amount, iw, ih);

  switch (type) {
    case "pixelate":
    case "square_bokeh": {
      const shape =
        type === "square_bokeh"
          ? "square"
          : options.pixelShape === "square" ||
              options.pixelShape === "circle" ||
              options.pixelShape === "diamond"
            ? options.pixelShape
            : "hex";
      return applyPixelMosaic(canvas, {
        amount,
        shape,
        gap: options.pixelGap ?? 0,
        softness: options.pixelSoftness ?? 0,
        mix: options.pixelMix ?? 100,
      });
    }
    case "motion":
      return applyMotionBlur(canvas, radius);
    case "bokeh":
      return applyFilterBlur(
        canvas,
        radius * 1.25,
        "saturate(1.18) brightness(1.04)"
      );
    case "box":
      return applyFilterBlur(canvas, radius * 0.85);
    case "disc":
      return applyFilterBlur(canvas, radius * 1.1, "contrast(1.06)");
    case "gaussian":
    default:
      return applyGaussianWithExtras(
        canvas,
        radius,
        options.gaussianMix ?? 100,
        options.edgePreserve ?? 0
      );
  }
}

/**
 * Approximate mosaic cell size for clearing under the subject.
 */
function estimateMosaicClearPx(options, iw, ih) {
  const amount = Math.max(0, Math.min(100, Math.round(Number(options.amount) || 0)));
  const shortSide = Math.min(iw, ih);
  const maxBlock = Math.max(16, Math.floor(shortSide / 5));
  const minBlock = 4;
  const block = Math.max(
    minBlock,
    Math.round(minBlock + (amount / 100) * (maxBlock - minBlock))
  );
  return Math.max(8, Math.ceil(block * 0.75));
}

/**
 * Hard white mask from cutout (optional expand). Any subject fringe becomes solid.
 */
function buildHardSubjectMask(cutoutCanvas, iw, ih, expandPx = 0) {
  const { canvas: raw, ctx: rctx } = createCanvas(iw, ih);
  rctx.clearRect(0, 0, iw, ih);
  rctx.drawImage(cutoutCanvas, 0, 0, iw, ih);

  let src = raw;
  const r = Math.max(0, Math.round(expandPx || 0));
  if (r > 0) {
    const { canvas: soft, ctx: sctx } = createCanvas(iw, ih);
    sctx.filter = "blur(" + r + "px)";
    sctx.drawImage(raw, 0, 0);
    sctx.filter = "none";
    src = soft;
  }

  const { canvas: out, ctx: octx } = createCanvas(iw, ih);
  const img = src.getContext("2d").getImageData(0, 0, iw, ih);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] > 8) {
      d[i] = 255;
      d[i + 1] = 255;
      d[i + 2] = 255;
      d[i + 3] = 255;
    } else {
      d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 0;
    }
  }
  octx.putImageData(img, 0, 0);
  return out;
}

/**
 * Punch subject out and fill the hole from neighboring background only
 * (no filter-blur soften — that reintroduced ghosts).
 */
function buildBackgroundOnlyPlate(sourceCanvas, cutoutCanvas, clearPx) {
  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const expand = Math.max(2, Math.ceil(clearPx * 0.45));
  const mask = buildHardSubjectMask(cutoutCanvas, iw, ih, expand);

  const { canvas: punched, ctx } = createCanvas(iw, ih);
  ctx.drawImage(sourceCanvas, 0, 0);
  ctx.globalCompositeOperation = "destination-out";
  ctx.drawImage(mask, 0, 0);
  ctx.globalCompositeOperation = "source-over";

  // Also hard-clear any leftover semi-transparent subject pixels.
  const img = ctx.getImageData(0, 0, iw, ih);
  const md = mask.getContext("2d").getImageData(0, 0, iw, ih).data;
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (md[i + 3] > 0) d[i + 3] = 0;
  }
  ctx.putImageData(img, 0, 0);

  return fillHolesFromNeighbors(punched);
}

/**
 * Fast hole fill via downscaled neighbor flood (background colors only).
 */
function fillHolesFromNeighbors(plateCanvas) {
  const iw = plateCanvas.width;
  const ih = plateCanvas.height;
  const maxWork = 560;
  const scale = Math.min(1, maxWork / Math.max(iw, ih));
  const ww = Math.max(1, Math.round(iw * scale));
  const wh = Math.max(1, Math.round(ih * scale));

  const { canvas: work, ctx: wctx } = createCanvas(ww, wh);
  wctx.drawImage(plateCanvas, 0, 0, ww, wh);
  const img = wctx.getImageData(0, 0, ww, wh);
  const d = img.data;

  for (let pass = 0; pass < 64; pass++) {
    const prev = new Uint8ClampedArray(d);
    let changed = 0;
    for (let y = 0; y < wh; y++) {
      for (let x = 0; x < ww; x++) {
        const i = (y * ww + x) * 4;
        if (prev[i + 3] > 16) continue;
        let rs = 0;
        let gs = 0;
        let bs = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= ww || ny >= wh) continue;
            const j = (ny * ww + nx) * 4;
            if (prev[j + 3] <= 16) continue;
            rs += prev[j];
            gs += prev[j + 1];
            bs += prev[j + 2];
            n += 1;
          }
        }
        if (!n) continue;
        d[i] = (rs / n) | 0;
        d[i + 1] = (gs / n) | 0;
        d[i + 2] = (bs / n) | 0;
        d[i + 3] = 255;
        changed += 1;
      }
    }
    if (!changed) break;
  }

  // Mean fallback for any remaining holes.
  let rs = 0;
  let gs = 0;
  let bs = 0;
  let n = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] > 16) {
      rs += d[i];
      gs += d[i + 1];
      bs += d[i + 2];
      n += 1;
    }
  }
  const mr = n ? (rs / n) | 0 : 160;
  const mg = n ? (gs / n) | 0 : 150;
  const mb = n ? (bs / n) | 0 : 155;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] <= 16) {
      d[i] = mr;
      d[i + 1] = mg;
      d[i + 2] = mb;
      d[i + 3] = 255;
    }
  }
  wctx.putImageData(img, 0, 0);

  const { canvas: filled, ctx: fctx } = createCanvas(iw, ih);
  fctx.imageSmoothingEnabled = true;
  fctx.drawImage(work, 0, 0, iw, ih);

  // Restore original sharp background outside the hole.
  const src = plateCanvas.getContext("2d").getImageData(0, 0, iw, ih);
  const out = fctx.getImageData(0, 0, iw, ih);
  const s = src.data;
  const o = out.data;
  for (let i = 0; i < s.length; i += 4) {
    if (s[i + 3] > 16) {
      o[i] = s[i];
      o[i + 1] = s[i + 1];
      o[i + 2] = s[i + 2];
      o[i + 3] = 255;
    } else {
      o[i + 3] = 255;
    }
  }
  fctx.putImageData(out, 0, 0);
  return filled;
}

/**
 * Background blur: clean plate → effect → keep mosaic/blur out from under subject → sharp cutout.
 */
export function composeBlurPrepCanvas(sourceCanvas, cutoutCanvas, options = {}) {
  const target = options.blurTarget || "full";
  const amount = Math.max(0, Math.round(Number(options.amount) || 0));
  const effectOpts = { ...options, amount };

  if (!cutoutCanvas || target === "full" || amount <= 0) {
    if (amount <= 0) {
      const iw = sourceCanvas.width;
      const ih = sourceCanvas.height;
      const { canvas, ctx } = createCanvas(iw, ih);
      ctx.drawImage(sourceCanvas, 0, 0);
      return canvas;
    }
    return renderBlurEffectCanvas(sourceCanvas, effectOpts);
  }

  const iw = sourceCanvas.width;
  const ih = sourceCanvas.height;
  const type = effectOpts.type || "gaussian";
  const isMosaic = type === "pixelate" || type === "square_bokeh";

  if (target === "background") {
    const clearPx = isMosaic
      ? estimateMosaicClearPx(effectOpts, iw, ih)
      : Math.max(
          8,
          Math.ceil(resolveBakeRadiusPx(amount, iw, ih) * 1.6)
        );

    // 1) Background-only plate (subject fully removed + hole filled)
    const plate = buildBackgroundOnlyPlate(sourceCanvas, cutoutCanvas, clearPx);
    // 2) Blur / pixelate that plate only
    const effected = renderBlurEffectCanvas(plate, effectOpts);

    const { canvas, ctx } = createCanvas(iw, ih);
    ctx.drawImage(effected, 0, 0);

    // 3) For mosaic: strip large cells from under the silhouette and replace
    //    with smooth filled plate so blocks can't chew into the shoe fringe.
    if (isMosaic) {
      const clearMask = buildHardSubjectMask(
        cutoutCanvas,
        iw,
        ih,
        Math.max(4, Math.ceil(clearPx * 0.85))
      );
      ctx.globalCompositeOperation = "destination-out";
      ctx.drawImage(clearMask, 0, 0);
      ctx.globalCompositeOperation = "destination-over";
      ctx.drawImage(plate, 0, 0);
      ctx.globalCompositeOperation = "source-over";
    }

    // 4) Sharp subject (keep natural cutout AA — don't over-harden)
    ctx.drawImage(cutoutCanvas, 0, 0, iw, ih);
    return canvas;
  }

  // Subject only: sharp punched scene + blurred cutout.
  const { canvas, ctx } = createCanvas(iw, ih);
  ctx.drawImage(sourceCanvas, 0, 0);
  ctx.globalCompositeOperation = "destination-out";
  ctx.drawImage(cutoutCanvas, 0, 0, iw, ih);
  ctx.globalCompositeOperation = "source-over";
  const blurredObject = renderBlurEffectCanvas(cutoutCanvas, effectOpts);
  ctx.drawImage(blurredObject, 0, 0, iw, ih);
  return canvas;
}

function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:") &&
      !url.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load preview image"));
    img.src = url;
  });
}

/**
 * Fast stage-sized preview bake for mosaic / effects that CSS can’t approximate.
 * @returns {Promise<string|null>} object URL
 */
export async function renderBlurLivePreviewUrl({
  sourceImageUrl,
  cutoutUrl = null,
  type = "gaussian",
  amount = 20,
  pixelShape = "hex",
  pixelGap = 0,
  pixelSoftness = 0,
  pixelMix = 100,
  blurTarget = "full",
  gaussianMix = 100,
  edgePreserve = 0,
  maxEdge = 720,
} = {}) {
  if (!sourceImageUrl || amount <= 0) return null;

  const source = await loadImageElement(sourceImageUrl);
  const sw = source.naturalWidth || source.width;
  const sh = source.naturalHeight || source.height;
  if (!sw || !sh) return null;

  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const ww = Math.max(1, Math.round(sw * scale));
  const wh = Math.max(1, Math.round(sh * scale));

  const { canvas: base } = createCanvas(ww, wh);
  base.getContext("2d").drawImage(source, 0, 0, ww, wh);

  const resolvedTarget =
    blurTarget || (cutoutUrl ? "subject" : "full");

  const effectOpts = {
    type,
    amount,
    pixelShape,
    pixelGap,
    pixelSoftness,
    pixelMix,
    blurTarget: resolvedTarget,
    gaussianMix,
    edgePreserve,
  };

  let cutWork = null;
  if (cutoutUrl) {
    const cutout = await loadImageElement(cutoutUrl);
    const made = createCanvas(ww, wh);
    made.ctx.drawImage(cutout, 0, 0, ww, wh);
    cutWork = made.canvas;
  }

  const composed = composeBlurPrepCanvas(base, cutWork, effectOpts);
  return canvasToObjectUrl(composed);
}

function canvasToObjectUrl(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not encode preview"));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      "image/png",
      0.92
    );
  });
}
